import { memo, useCallback, useState } from 'react';
import type { CreateBookingRequest, TableDTO } from '@six-pockets/shared';
import { tablesApi } from '@/api';
import { EightBallIcon, PlayIcon, PlusIcon } from '@/components/icons';
import { Button, Spinner, StatusPill } from '@/components/ui';
import { useTableActions } from '@/context/TablesContext';
import { BookingFormModal } from '@/features/bookings/components/BookingFormModal';
import { cx } from '@/utils/cx';
import { TABLE_STATUS } from '../status';
// Cup button parked for now (see CondimentsPopover.tsx).
// import { CondimentsPopover } from './CondimentsPopover';
import { SelectionBar } from './SelectionBar';
import { BookingList } from './BookingList';
import styles from '@/styles/components/TableCard/style.scss';

interface TableCardProps {
  table: TableDTO;
  /** This table's ticked bookings (the same array while unchanged, so the card can skip re-rendering). */
  selectedIds: readonly string[];
  /** Opens the bill for the ticked bookings. Omitted when bookings are ticked on other tables too (the page header has it). */
  onGenerateBill?: () => void;
}

/** One table. Memoised: it re-renders only when its table, its ticked bookings or the bill placement change. */
export const TableCard = memo(function TableCard({ table, selectedIds, onGenerateBill }: TableCardProps) {
  const { startTable, replaceTables, clearSelection } = useTableActions();
  const status = TABLE_STATUS[table.status];
  const busy = table.status !== 'available';

  // Add booking: for play that began while staff were away (errors show in the modal).
  const [adding, setAdding] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const closeAdd = useCallback(() => {
    if (addBusy) return;
    setAdding(false);
    setAddError(null);
  }, [addBusy]);

  // START shows a spinner and stays disabled until the server answers (no double starts).
  const [starting, setStarting] = useState(false);
  const start = async () => {
    if (starting) return;
    setStarting(true);
    try {
      await startTable(table.id);
    } finally {
      setStarting(false);
    }
  };

  const addBooking = async (body: CreateBookingRequest) => {
    setAddBusy(true);
    setAddError(null);
    try {
      replaceTables([await tablesApi.addBooking(table.id, body)]);
      setAdding(false);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Could not add the booking');
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <article className={cx(styles.card, styles[table.status])} aria-label={table.name}>
      <header className={styles.header}>
        <div className={styles.titles}>
          <div className={styles.nameRow}>
            {/* A reminder for staff of which card is the pool table. */}
            {/pool/i.test(table.kind) && <EightBallIcon className={styles.kindIcon} />}
            <h2 className={styles.name}>{table.name}</h2>
            <StatusPill tone={status.tone} label={status.label} hollow={table.status === 'available'} />
          </div>
          <span className={styles.kind}>{table.kind}</span>
        </div>
        <Button
          size="lg"
          iconOnly
          icon={<PlusIcon size={18} />}
          aria-label={`Add a booking to ${table.name}`}
          title="Add a booking that already started"
          onClick={() => setAdding(true)}
        />
        <Button
          variant="go"
          size="lg"
          icon={starting ? <Spinner size={16} /> : <PlayIcon size={16} />}
          disabled={busy || starting}
          aria-busy={starting}
          aria-label={`Start ${table.name}`}
          title={busy ? 'Stop or end the current booking first' : undefined}
          onClick={() => void start()}
        >
          START
        </Button>
      </header>

      <BookingList tableId={table.id} bookings={table.bookings} selectedIds={selectedIds} />

      <footer className={styles.footer}>
        <SelectionBar
          count={selectedIds.length}
          onClear={() => clearSelection(table.id)}
          onGenerateBill={onGenerateBill}
        />
      </footer>

      {/* <CondimentsPopover table={table} /> */}

      {adding && <BookingFormModal table={table} busy={addBusy} error={addError} onAdd={(body) => void addBooking(body)} onClose={closeAdd} />}
    </article>
  );
});
